import toastr from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import {
  generateAppendQueryParam,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  SingleBoardNodesResponse,
  BoardNodesAttribute,
  BoardNodesResponse,
  BoardNodes,
} from 'types/BoardNode';

type MutationVariables = Partial<BoardNodesAttribute> & { id?: string };

export function useUpdateBoardNode(
  boardNodeID: string | null | undefined,
  boardID: string
) {
  const queryClient = useQueryClient();
  const updateBoardNode = (variables: MutationVariables) => {
    const { id, ...rest } = variables;
    const nodeID = boardNodeID || id;
    if (!nodeID) {
      return Promise.reject('board ID can not be null');
    }
    const queryArray = [generateAppendQueryParam('includeData', true)];

    const url = generateURL(
      SPARK_RESOURCE_TYPES.BOARD_NODES,
      queryArray,
      nodeID
    );

    const body = {
      data: {
        id: nodeID,
        type: SPARK_RESOURCE_TYPES.BOARD_NODES,
        attributes: rest,
      },
    };
    return fetchWrapper<SingleBoardNodesResponse>({
      url: url,
      method: 'PATCH',
      body,
    });
  };

  return useMutation(
    [SPARK_RESOURCE_TYPES.BOARD_NODES, boardNodeID],
    updateBoardNode,
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to get Board Nodes: ${error.message}`);
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries([
          SPARK_RESOURCE_TYPES.BOARD_NODES,
          boardID,
        ]);
      },
      onMutate: (data) => {
        const { data: editorData, ...rest } = data;
        queryClient.setQueryData<BoardNodesResponse>(
          [SPARK_RESOURCE_TYPES.BOARD_NODES, boardID],
          (oldValues) => {
            const updatingNodeID = boardNodeID || rest.id;
            const updatedRecords = oldValues?.data
              .map((item) => {
                if (item.id === updatingNodeID) {
                  if (data.isDeleted) {
                    return;
                  }
                  return {
                    ...item,
                    attributes: {
                      ...item.attributes,
                      ...rest,
                    },
                    meta: {
                      ...item.meta,
                      data: editorData,
                    },
                  };
                }
                return item;
              })
              .filter((item): item is BoardNodes => !!item);
            return { ...oldValues, data: updatedRecords || [] };
          }
        );
      },
    }
  );
}
